import { type ReactNode, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import type { NavLabels, SharedProps } from '@/types'
import { AccountIcon, DashboardIcon, EstablishmentIcon, KegIcon } from '@/components/nav/icons'
import { Button } from '@/components/ui/Button'
import { BRAND_NAME } from '@/lib/brand'

const NAV_ITEMS = [
  { href: '/dashboard', key: 'dashboard', icon: DashboardIcon },
  { href: '/kegs', key: 'kegs', icon: KegIcon },
  { href: '/establishments', key: 'establishments', icon: EstablishmentIcon },
] as const satisfies ReadonlyArray<{ href: string; key: keyof NavLabels; icon: typeof DashboardIcon }>

// "/" is the public landing page (LandingController), never the dashboard —
// authenticated nav must never link there or it bounces the user out of the app shell.
function isActive(url: string, href: string) {
  return url === href || url.startsWith(`${href}/`)
}

function brewerySettingsHref(slug: string | undefined) {
  return slug ? `/organizations/${slug}` : '#'
}

function BrandMark() {
  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-ink">
      <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
        <path d="M4 2.5h8l-.6 10.2a1.5 1.5 0 0 1-1.5 1.4H6.1a1.5 1.5 0 0 1-1.5-1.4L4 2.5Z" fill="#FBF4E4" />
        <path d="M4.2 6.2h7.6" stroke="#E2461F" strokeWidth="1.5" />
      </svg>
    </span>
  )
}

function Wordmark() {
  const [firstWord, ...restWords] = BRAND_NAME.split(' ')
  return (
    <span className="flex items-center gap-2 font-body text-lg font-extrabold tracking-tight text-ink">
      <BrandMark />
      {firstWord} {restWords.length > 0 && <span className="text-flame">{restWords.join(' ')}</span>}
    </span>
  )
}

function AccountMenu({ auth, nav }: { auth: SharedProps['auth']; nav: NavLabels }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(true)}
        aria-label={nav.account}
        className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-paper-2"
      >
        <AccountIcon className="h-6 w-6" />
      </button>

      {open && (
        <>
          <button aria-label={nav.close_menu} onClick={() => setOpen(false)} className="fixed inset-0 z-40 cursor-default" />
          <div className="absolute right-0 z-50 mt-2 w-64 rounded-2xl border border-line bg-card p-4 shadow-soft">
            <p className="font-bold text-ink">{auth.organization?.name}</p>
            <Link href={brewerySettingsHref(auth.organization?.slug)} className="mt-1 block text-sm font-semibold text-flame hover:text-flame-deep">
              {nav.brewery_settings}
            </Link>
            <p className="mt-4 truncate text-sm text-ink-soft">{auth.user?.email_address}</p>
            <Button
              variant="ghost"
              onClick={() => router.delete('/session')}
              className="mt-3 w-full"
            >
              {nav.logout}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

function FlashMessages() {
  const { flash } = usePage<SharedProps>().props
  return (
    <>
      {flash.notice && (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mt-4 rounded-xl bg-leaf-tint px-4 py-3 text-sm font-semibold text-leaf-deep">{flash.notice}</div>
        </div>
      )}
      {flash.alert && (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mt-4 rounded-xl bg-flame-tint px-4 py-3 text-sm font-semibold text-flame-deep">{flash.alert}</div>
        </div>
      )}
    </>
  )
}

export default function AuthenticatedLayout({ children }: { children: ReactNode }) {
  const { auth, nav } = usePage<SharedProps>().props
  const currentUrl = usePage().url

  if (!nav) return <>{children}</>

  return (
    <div className="min-h-screen bg-paper font-body">
      {/* Desktop nav */}
      <nav className="sticky top-0 z-30 hidden border-b-2 border-ink bg-paper/[0.88] backdrop-blur-[8px] md:block">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-8">
              <Link href="/dashboard">
                <Wordmark />
              </Link>
              <div className="flex items-center gap-6">
                {NAV_ITEMS.map((item) => {
                  const active = isActive(currentUrl, item.href)
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`relative py-1 text-sm font-semibold transition-colors ${active ? 'text-ink' : 'text-ink-soft hover:text-ink'}`}
                    >
                      {nav[item.key]}
                      {active && <span className="absolute -bottom-[1px] left-0 right-0 h-[3px] rounded-full bg-flame" />}
                    </Link>
                  )
                })}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link href={brewerySettingsHref(auth.organization?.slug)} className="text-sm font-bold text-ink hover:text-flame">
                {auth.organization?.name}
              </Link>
              <span className="text-sm text-ink-faint">{auth.user?.email_address}</span>
              <Button variant="ghost" onClick={() => router.delete('/session')} className="px-3 py-1.5 text-xs">
                {nav.logout}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile top bar */}
      <nav className="flex h-14 items-center justify-between border-b-2 border-ink bg-paper px-4 md:hidden">
        <Link href="/dashboard">
          <Wordmark />
        </Link>
        <AccountMenu auth={auth} nav={nav} />
      </nav>

      <FlashMessages />

      <main className="pb-20 md:pb-0">{children}</main>

      {/* Mobile bottom tab bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex border-t-2 border-ink bg-paper pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label={nav.primary_nav}
      >
        {NAV_ITEMS.map((item) => {
          const active = isActive(currentUrl, item.href)
          const Icon = item.icon
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-bold ${active ? 'text-flame' : 'text-ink-faint'}`}
            >
              <Icon className="h-6 w-6" />
              {nav[item.key]}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}
